<script setup>

import { reactive, ref, computed, onMounted, watch } from 'vue';

import { useAppStore } from 'store/store';
const { csrf, countries, messengers } = useAppStore();

import use$T  from 'cfg/useTranslation';
const $t = use$T();

import errors       from 'cfg/errorBag';
import useRecaptcha from 'cfg/recaptcha';

import Auth        from '@Auth/Auth';
import MultiSelect from 'Common/MultiSelect/MultiSelect.vue';

const props = defineProps({
  role          : { type: String, default: 'advertiser' },
  recaptchaKey  : { type: String, default: '' },
  mondiadApiUrl : { type: String, default: '' },
});

const getRecaptchaToken = useRecaptcha(props.recaptchaKey);

const roles = [
  { value: 'advertiser', title: 'roles.advertiser.title', sub: 'roles.advertiser.sub' },
  { value: 'publisher',  title: 'roles.publisher.title',  sub: 'roles.publisher.sub' },
];

function readContext() {
  const qs = new URLSearchParams(window.location.search);
  const by = parseInt(qs.get('by'), 10);
  return {
    source       : qs.get('source') || qs.get('utm_source') || qs.get('utm') || undefined,
    referral     : qs.get('referral') || undefined,
    introducedBy : Number.isNaN(by) ? undefined : by,
    httpReferrer : document.referrer || undefined,
  };
}
const ctx = readContext();

const form = reactive({
  role                : props.role === 'publisher' ? 'publisher' : 'advertiser',
  accountType         : 'individual',
  companyName         : '',
  addressFirstLine    : '',
  addressSecondLine   : '',
  email               : '',
  confirmEmail        : '',
  password            : '',
  confirmPassword     : '',
  firstName           : '',
  lastName            : '',
  country             : '',
  phone               : '',
  messengerId         : '',
  messengerTypeId     : '',
  sourceInfoAboutUs   : '',
  vertical            : '',
  signUpForNewsletter : false,
  agreement           : false,
});

const state = reactive({ submitting: false, sent: false, sentEmail: '' });

const isCompany    = computed(()=> form.accountType === 'company');
const isAdvertiser = computed(()=> form.role === 'advertiser');

const sourceOptions = ['Forum', 'Search Engine', 'Conference', 'Social Media', 'Linkedin', 'Blog', 'Other'];


function emitRole(role) {
  window.dispatchEvent(new CustomEvent('mondiad:register-role', { detail: { role } }));
}

watch(()=> form.role, (role)=> emitRole(role));

onMounted(async ()=> {
  emitRole(form.role);
  form.country = await Auth.autoDetectCountry(props.mondiadApiUrl);
});


function err(field) {
  return errors.first(field) || '';
}


async function google() {
  const { url } = await Auth.google({
    role    : form.role,
    country : form.country || undefined,
    csrf    : csrf['auth::user-register'],
  });

  if (url) window.location.href = url;
}

async function submit() {

  if (state.submitting) return;
  errors.clear();

  state.submitting = true;
  const data = await Auth.register({
    csrf: csrf['auth::user-register'],
    captchaCode         : await getRecaptchaToken('register') ,
    role                : form.role,
    accountType         : form.accountType,
    companyName         : form.companyName,
    addressFirstLine    : form.addressFirstLine,
    addressSecondLine   : form.addressSecondLine,
    email               : form.email,
    confirmEmail        : form.confirmEmail,
    password            : form.password,
    confirmPassword     : form.confirmPassword,
    firstName           : form.firstName,
    lastName            : form.lastName,
    country             : form.country || undefined,
    phone               : form.phone,
    messengerId         : form.messengerId,
    messengerTypeId     : form.messengerTypeId || undefined,
    sourceInfoAboutUs   : form.sourceInfoAboutUs,
    vertical            : form.vertical,
    signUpForNewsletter : form.signUpForNewsletter,
    agreement           : form.agreement,

    source       : ctx.source,
    referral     : ctx.referral,
    introducedBy : ctx.introducedBy,
    httpReferrer : ctx.httpReferrer,
  }).finally(()=> state.submitting = false);

  state.sentEmail = data.email || form.email.trim();
  state.sent = true;
}

</script>

<template>
  <div class="reg-card">

    <template v-if="!state.sent">
      <div class="role-tabs">
        <button
          v-for="r in roles"
          :key="r.value"
          type="button"
          class="role-tab"
          :class="{ active: form.role === r.value }"
          @click="form.role = r.value"
        >
          <span class="role-tab-title">{{ $t(`@${r.title}`) }}</span>
          <span class="role-tab-desc">{{ $t(`@${r.sub}`) }}</span>
        </button>
      </div>


      <button class="btn-google" @click="google">
        <i class="fa-brands fa-google" aria-hidden="true" />
        <span>{{ $t('@form.google') }}</span>
      </button>


      <div class="form-divider">{{ $t('@form.divider') }}</div>

      <form novalidate @submit.prevent="submit">

        <div v-if="errors.has('global')" class="reg-form-err">{{ errors.first('global') }}</div>

        <div class="reg-radio">
          <label><input type="radio" v-model="form.accountType" value="individual"> <span>{{ $t('@form.individual') }}</span></label>
          <label><input type="radio" v-model="form.accountType" value="company"> <span>{{ $t('@form.company') }}</span></label>
        </div>

        <template v-if="isCompany">
          <div class="field">
            <input type="text" v-model="form.companyName" :placeholder="$t('@form.company_name')">
            <small class="field-err">{{ err('companyName') }}</small>
          </div>
          <div class="field-grid">
            <div class="field"><input type="text" v-model="form.addressFirstLine" :placeholder="$t('@form.address1')"></div>
            <div class="field"><input type="text" v-model="form.addressSecondLine" :placeholder="$t('@form.address2')"></div>
          </div>
        </template>

        <div class="field-grid">
          <div class="field">
            <input type="email" v-model="form.email" autocomplete="new-email" :placeholder="$t('@form.email')">
            <small class="field-err">{{ err('email') }}</small>
          </div>
          <div class="field">
            <input type="email" v-model="form.confirmEmail" autocomplete="off" :placeholder="$t('@form.confirm_email')">
            <small class="field-err">{{ err('confirmEmail') }}</small>
          </div>
        </div>

        <div class="field-grid">
          <div class="field">
            <input type="password" v-model="form.password" autocomplete="new-password" :placeholder="$t('@form.password')">
            <small class="field-err">{{ err('password') }}</small>
          </div>
          <div class="field">
            <input type="password" v-model="form.confirmPassword" autocomplete="new-password" :placeholder="$t('@form.confirm_password')">
            <small class="field-err">{{ err('confirmPassword') }}</small>
          </div>
        </div>

        <div class="field-grid">
          <div class="field">
            <input type="text" v-model="form.firstName" :placeholder="$t('@form.first_name')">
            <small class="field-err">{{ err('firstName') }}</small>
          </div>
          <div class="field">
            <input type="text" v-model="form.lastName" :placeholder="$t('@form.last_name')">
            <small class="field-err">{{ err('lastName') }}</small>
          </div>
        </div>

        <div class="field-grid">
          <div class="field">
            <MultiSelect v-model="form.country"
              :options="countries"
              search
              value="id"
              label="name"
              :title="$t('@form.country')"
            />
            <small class="field-err">{{ err('countryId') }}</small>
          </div>
          <div class="field">
            <input type="tel" v-model="form.phone" :placeholder="$t('@form.phone')">
            <small class="field-err">{{ err('phone') }}</small>
          </div>
        </div>

        <div class="field-grid im-grid">
          <div class="field">
            <input type="text" v-model="form.messengerId" :placeholder="$t('@form.messenger_id')">
            <small class="field-err">{{ err('messengerId') }}</small>
          </div>
          <div class="field">
            <select v-model="form.messengerTypeId">
              <option value="">{{ $t('@form.messenger') }}</option>
              <option v-for="m in messengers" :key="m.id" :value="m.id">{{ m.name }}</option>
            </select>
            <small class="field-err">{{ err('messengerTypeId') }}</small>
          </div>
        </div>

        <div class="field">
          <select v-model="form.sourceInfoAboutUs">
            <option value="">{{ $t('@form.source') }}</option>
            <option v-for="s in sourceOptions" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>

        <div v-if="isAdvertiser" class="field">
          <input type="text" v-model="form.vertical" :placeholder="$t('@form.vertical')">
          <small class="field-err">{{ err('vertical') }}</small>
        </div>

        <label class="reg-check">
          <input type="checkbox" v-model="form.signUpForNewsletter">
          <span>{{ $t('@form.newsletter') }}</span>
        </label>

        <label class="reg-check">
          <input type="checkbox" v-model="form.agreement">
          <span v-html="$t('@form.agreement')"></span>
        </label>
        <small class="field-err">{{ err('agreement') }}</small>

        <vue-ladda
          type="submit"
          data-style="expand-right"
          class="btn btn-primary reg-submit"
          :loading="state.submitting"
          :disabled="!form.agreement"
        >
          {{ $t('@form.submit') }}
        </vue-ladda>
      </form>
    </template>

    <div v-if="state.sent" class="reg-success">
      <div class="reg-success-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h4>{{ $t('@success.title') }}</h4>
      <p><span v-html="$t('@success.body', { email: state.sentEmail })"></span></p>
    </div>
  </div>
</template>

<style lang="sass" scoped>

.role-tabs
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 10px
  margin-bottom: 24px

.role-tab
  padding: 16px
  border-radius: var(--r-md, 12px)
  border: 1px solid var(--line-2, #E5E7E2)
  background: var(--paper, #fff)
  text-align: left
  cursor: pointer
  font-family: inherit
  transition: all .15s ease

  &.active
    border-color: var(--brand-400)
    background: var(--brand-50)

.role-tab-title
  display: block
  font-family: var(--display, sans-serif)
  font-weight: 600
  font-size: 16px
  margin-bottom: 4px

.role-tab.active .role-tab-title
  color: var(--brand-700)

.role-tab-desc
  display: block
  font-size: 12px
  color: var(--muted, #6B7770)
  line-height: 1.4

.btn-google
  width: 100%
  display: flex
  align-items: center
  justify-content: center
  gap: 10px
  padding: 12px 16px
  background: #fff
  color: #1F1F1F
  border: 1px solid var(--line-2)
  border-radius: var(--r-sm)
  font-family: inherit
  font-size: 14px
  font-weight: 600
  letter-spacing: -.01em
  cursor: pointer
  transition: background .15s ease, border-color .15s ease, box-shadow .15s ease
  margin-bottom: 16px

  &:hover
    background: #F8F9FA
    border-color: var(--ink-2)
    box-shadow: 0 1px 2px rgba(10, 22, 18, .06)

  svg
    flex-shrink: 0

  .fa-google
    flex-shrink: 0
    font-size: 16px
    color: #4285F4

.form-divider
  display: flex
  align-items: center
  gap: 12px
  margin: 16px 0
  font-size: 11px
  font-weight: 600
  color: var(--muted, #6B7770)
  text-transform: uppercase
  letter-spacing: .1em

  &::before, &::after
    content: ""
    flex: 1
    height: 1px
    background: var(--line, #E5E7E2)

.reg-radio
  display: flex
  gap: 20px
  margin-bottom: 16px
  font-size: 14px

  label
    display: flex
    align-items: center
    gap: 8px
    cursor: pointer

.field
  margin-bottom: 14px

  input, select
    width: 100%
    padding: 12px 14px
    font-family: inherit
    font-size: 15px
    color: var(--ink, #0A1612)
    background: var(--paper, #fff)
    border: 1px solid var(--line-2, #E5E7E2)
    border-radius: var(--r-sm, 8px)
    transition: border-color .15s ease, box-shadow .15s ease

  input:focus, select:focus
    outline: none
    border-color: var(--brand-400)
    box-shadow: 0 0 0 3px var(--brand-50)

.field-grid
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 12px

  &.im-grid
    grid-template-columns: 2fr 1fr

@media (max-width: 560px)
  .field-grid
    grid-template-columns: 1fr

.field-err
  display: block
  color: #dc2626
  font-size: 12px
  margin-top: 4px

  &:empty
    display: none

.reg-form-err
  background: #fef2f2
  border: 1px solid #fecaca
  color: #b91c1c
  border-radius: 8px
  padding: 10px 12px
  font-size: 13px
  margin-bottom: 14px

.reg-check
  display: flex
  align-items: flex-start
  gap: 8px
  margin: 6px 0 4px
  font-size: 13px
  color: var(--ink-2, #4a5568)
  cursor: pointer

  input
    margin-top: 2px

  :deep(a)
    color: var(--brand-600)
    text-decoration: underline

.reg-submit
  width: 100%
  justify-content: center
  margin-top: 16px
  padding: 14px
  font-size: 15px

  &[disabled], &[data-loading]
    opacity: .6
    cursor: not-allowed

.reg-success
  text-align: center
  padding: 24px 16px
  background: var(--accent-50, #eafaf0)
  border: 1px solid var(--accent-100, #bfe9cf)
  border-radius: var(--r-md, 12px)

.reg-success-icon
  width: 48px
  height: 48px
  margin: 0 auto 14px
  border-radius: 50%
  background: var(--accent-400)
  color: #fff
  display: grid
  place-items: center

  h4
    font-size: 18px
    margin-bottom: 8px

.reg-success h4
  font-size: 18px
  margin-bottom: 8px

.reg-success p
  font-size: 14px
  color: var(--ink-2, #4a5568)
  line-height: 1.6

</style>
